import React, { useState } from 'react';
import { FileText, Image, Shield, ShieldOff, ImageOff, ChevronDown, ChevronRight } from 'lucide-react';

// Shared email UI utilities for VirgoMail views
// NOTE: HTTP utilities (authFetch, getAuthHeaders) have been removed.
// Use emailService.js for all API calls.

export const getTypeIcon = (type) => {
  switch(type) {
    case 'court_filing': return '⚖️';
    case 'client_inquiry': return '👤';
    case 'opposing_counsel': return '⚔️';
    case 'vendor': return '📦';
    default: return '📧';
  }
};

export const getUrgencyBorder = (urgency) => {
  switch(urgency) {
    case 'critical': return 'border-l-4 border-l-red-500';
    case 'high': return 'border-l-4 border-l-orange-500';
    case 'normal': return 'border-l-4 border-l-blue-500';
    default: return 'border-l-4 border-l-gray-600';
  }
};

export const urgencyBadgeClass = (urgency) => {
  switch(urgency) {
    case 'critical': return 'bg-red-500/20 text-red-300 border border-red-500/30';
    case 'high': return 'bg-orange-500/20 text-orange-300 border border-orange-500/30';
    default: return 'bg-blue-500/20 text-blue-300 border border-blue-500/30';
  }
};

export const getDuplicateBadge = (email) => {
  if (!email.is_duplicate || !email.duplicate_match_type) return null;
  const labels = { message_id: 'Exact Match', content_hash: 'Content Match', subject_sender_time: 'Similar' };
  return (
    <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 rounded text-xs">
      {labels[email.duplicate_match_type] || 'Duplicate'}
    </span>
  );
};

/**
 * Extract a display-friendly sender name from a raw RFC 2822 From header.
 *
 * Handles formats:
 *   "Display Name" <email@example.com>  → Display Name
 *   Display Name <email@example.com>    → Display Name
 *   <email@example.com>                 → email@example.com
 *   email@example.com                   → email@example.com
 *
 * @param {object} email - The email object from the API
 * @returns {string} Display name for the sender
 */
export const parseSenderName = (email) => {
  const raw = email?.from_addr || email?.from || email?.sender || '';
  if (!raw) return 'Unknown';

  // If it has angle brackets: extract name before < and email inside <>
  if (raw.includes('<')) {
    const name = raw.split('<')[0].trim().replace(/^["']|["']$/g, '');
    if (name) return name;
    // No name before <, extract email from angle brackets
    const emailMatch = raw.match(/<([^>]+)>/);
    return emailMatch ? emailMatch[1] : raw;
  }

  // Plain email address or name — return as-is
  return raw.trim();
};

/**
 * Format byte count into human-readable file size.
 */
export const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

/**
 * Return a lucide icon element appropriate for the given MIME content type.
 */
export const getFileIcon = (contentType) => {
  if (contentType?.startsWith('image/')) return <Image className="w-4 h-4" />;
  return <FileText className="w-4 h-4" />;
};

/**
 * Click handler for <a> elements inside rendered email bodies.
 * Intercepts navigation and opens links in a new tab safely.
 * Attach via addEventListener('click', handleLinkClick) on the body container.
 */
export const handleLinkClick = (e) => {
  const link = e.target.closest('a');
  if (link && link.href) {
    e.preventDefault();
    e.stopPropagation();
    window.open(link.href, '_blank', 'noopener,noreferrer');
  }
};

// CSS injected into HTML email iframes to style quoted content
const QUOTE_STYLES = `
blockquote, .gmail_quote, .yahoo_quoted, .moz-cite-prefix + blockquote,
div[class*="quote"], div[class*="Reply"], div[class*="reply"] {
  border-left: 3px solid #cbd5e1 !important;
  padding-left: 12px !important;
  margin: 12px 0 !important;
  color: #64748b !important;
}
.gmail_attr, .gmail_quote > div:first-child,
div[class*="wrote"], .moz-cite-prefix {
  color: #94a3b8 !important;
  font-size: 12px !important;
  margin-bottom: 4px !important;
}
blockquote blockquote, .gmail_quote .gmail_quote {
  border-left-color: #e2e8f0 !important;
  color: #94a3b8 !important;
}
`;

// P6 dim pass — the FRAME (iframe element) carries brightness(0.9) contrast(0.96)
// to take the blinding white off any email. Here we (a) give the iframe canvas a
// warm near-white paper tone so unstyled areas aren't pure white, and (b) apply the
// INVERSE filter to media (img/picture/video/background-image) so the frame dim
// does NOT wash photos or logos. We never strip or override the email's own colors.
const DIM_STYLES = `
html{background:#F3EFE7 !important;}
img,picture,video,[style*="background-image"],[background]{filter:brightness(1.1111) contrast(1.0417);}
`;

// Fixed-width emails (Outlook / newsletters, ~600px) render left-aligned in a wide
// pane, leaving it half-empty. Webmail behavior: keep the pane full-width and CENTER
// the fixed-width block — never force-stretch. margin-inline:auto centers a top-level
// table/div only when it has a set width < the pane; a width:100% wrapper is untouched.
const CENTER_STYLES = `
body>table,body>div,body>center>table,body>center>div{margin-left:auto !important;margin-right:auto !important;}
`;

/**
 * Build a complete HTML document suitable for an iframe srcDoc,
 * with quote styling and optional remote image stripping.
 *
 * @param {string} body - raw HTML email body
 * @param {boolean} showRemoteImages - whether to keep remote <img> tags
 * @returns {string} full HTML document string
 */
export const buildHtmlSrcDoc = (body, showRemoteImages = false) => {
  let processedBody = body;
  if (!showRemoteImages) {
    processedBody = processedBody.replace(/<img[^>]+src=["']https?:\/\/[^"']+["'][^>]*\/?>/gi, '');
  }
  const trimmed = processedBody.trim().toLowerCase();
  const isFullHtmlDoc = trimmed.startsWith('<!doctype') || trimmed.startsWith('<html') || trimmed.startsWith('<?xml');

  if (isFullHtmlDoc) {
    const styleTag = `<style>img{max-width:100%!important;height:auto!important;}${QUOTE_STYLES}${DIM_STYLES}${CENTER_STYLES}</style>`;
    if (processedBody.toLowerCase().includes('</head>')) {
      return processedBody.replace(/<\/head>/i, styleTag + '</head>');
    }
    return styleTag + processedBody;
  }

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
body{font-family:Arial,sans-serif;font-size:14px;line-height:1.6;color:#333;margin:16px;background:#F3EFE7;}
img{max-width:100%;height:auto;}p{margin-bottom:0.5em;}a{color:#2563eb;}
${QUOTE_STYLES}${DIM_STYLES}${CENTER_STYLES}
</style></head><body>${processedBody}</body></html>`;
};

/**
 * Parse a plain-text email body into segments: original content vs quoted
 * reply/forward text. Used to render collapsible quote blocks.
 *
 * @param {string} text - plain text email body
 * @returns {Array<{type: 'original'|'quoted', content: string}>}
 */
export const parseEmailSegments = (text) => {
  if (!text) return [{ type: 'original', content: text }];

  const lines = text.split('\n');
  let quoteSplitIndex = -1;

  const quoteHeaderPatterns = [
    /^-{3,}\s*(Original|Forwarded)\s*(Message|message)\s*-{3,}/,
    /^_{3,}\s*$/,
    /^On\s+.{10,}\s+wrote:\s*$/,
    /^On\s+.{10,}\s+<.+@.+>\s+wrote:\s*$/,
    /^From:\s+.+$/,
    /^>{1,}\s*On\s+.+wrote:/,
    /^\*From:\*\s+.+$/,
  ];

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();

    for (const pattern of quoteHeaderPatterns) {
      if (pattern.test(trimmed)) {
        if (/^From:\s+.+$/.test(trimmed) || /^\*From:\*\s+.+$/.test(trimmed)) {
          const nextLines = lines.slice(i + 1, i + 5).map(l => l.trim());
          const hasEmailHeaders = nextLines.some(l =>
            /^(Sent|Date|To|Subject|Cc):\s+.+/.test(l) || /^\*(Sent|Date|To|Subject|Cc):\*\s+.+/.test(l)
          );
          if (!hasEmailHeaders) continue;
        }
        quoteSplitIndex = i;
        break;
      }
    }
    if (quoteSplitIndex >= 0) break;

    if (/^>/.test(trimmed) && i > 0) {
      let quoteCount = 0;
      for (let j = i; j < Math.min(i + 5, lines.length); j++) {
        if (/^>/.test(lines[j].trim()) || lines[j].trim() === '') quoteCount++;
      }
      if (quoteCount >= 3) {
        quoteSplitIndex = i;
        break;
      }
    }
  }

  if (quoteSplitIndex < 0) {
    return [{ type: 'original', content: text }];
  }

  const originalText = lines.slice(0, quoteSplitIndex).join('\n').replace(/\n{3,}$/g, '\n');
  const quotedText = lines.slice(quoteSplitIndex).join('\n');

  const result = [];
  if (originalText.trim()) result.push({ type: 'original', content: originalText });
  if (quotedText.trim()) result.push({ type: 'quoted', content: quotedText });

  return result.length > 0 ? result : [{ type: 'original', content: text }];
};

/**
 * Collapsible quoted-text block for plain-text email rendering.
 */
export const QuotedBlock = ({ content, linkifyText }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mt-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-400 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
      >
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {expanded ? 'Hide quoted text' : 'Show quoted text'}
        <span className="text-slate-400 ml-1">···</span>
      </button>
      {expanded && (
        <div className="mt-2 pl-3 border-l-[3px] border-slate-300 text-slate-500 text-sm whitespace-pre-wrap font-sans leading-relaxed">
          {linkifyText(content)}
        </div>
      )}
    </div>
  );
};

/**
 * Remote image privacy banner. Shows a toggle to load/block remote images
 * in HTML emails.
 *
 * @param {boolean} showRemoteImages - current toggle state
 * @param {Function} setShowRemoteImages - state setter
 * @param {string} emailBody - the HTML body (used to detect if remote images exist)
 * @returns {React.ReactNode|null}
 */
export const RemoteImageBanner = ({ showRemoteImages, setShowRemoteImages, emailBody }) => {
  const hasRemoteImages = (emailBody?.match(/src=["']https?:\/\//gi) || []).length > 0;
  if (!hasRemoteImages) return null;

  if (!showRemoteImages) {
    return (
      <div className="flex items-center justify-between bg-amber-500/10 border-b border-amber-500/30 px-4 py-2">
        <div className="flex items-center gap-2 text-amber-400 text-sm">
          <Shield className="w-4 h-4" />
          <span>Remote images are blocked for privacy</span>
        </div>
        <button onClick={() => setShowRemoteImages(true)}
          className="flex items-center gap-1.5 px-3 py-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded text-xs font-medium transition-colors">
          <Image className="w-3 h-3" /> Load Images
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between bg-green-500/10 border-b border-green-500/30 px-4 py-2">
      <div className="flex items-center gap-2 text-green-400 text-sm">
        <ShieldOff className="w-4 h-4" />
        <span>Remote images are loaded</span>
      </div>
      <button onClick={() => setShowRemoteImages(false)}
        className="flex items-center gap-1.5 px-3 py-1 bg-slate-500/20 hover:bg-slate-500/30 text-slate-300 rounded text-xs font-medium transition-colors">
        <ImageOff className="w-3 h-3" /> Block Images
      </button>
    </div>
  );
};

/**
 * Convert URLs in plain text into clickable <a> elements.
 */
export const linkifyText = (text) => {
  if (!text) return text;
  const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (part.match(urlRegex)) {
      return (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline break-all"
          onClick={(e) => e.stopPropagation()}>
          {part}
        </a>
      );
    }
    return part;
  });
};
